import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { dato, idag } from '../lib/format';
import { PageHeader, Card } from '../components/ui';
import { Download, Lock, Unlock, Plus, Trash2, FileText, Send, CheckCircle2, AlertTriangle } from 'lucide-react';

const Compliance = () => {
  const aar = new Date().getFullYear();
  const [fra, setFra] = useState(`${aar}-01-01`);
  const [til, setTil] = useState(idag());

  const [perioder, setPerioder] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [nyPeriode, setNyPeriode] = useState({ fra_dato: '', til_dato: '', beskrivelse: '' });
  const [altinnStatus, setAltinnStatus] = useState(null);

  const last = async () => {
    const [p, a] = await Promise.all([api.get('/perioder'), api.get('/audit', { params: { limit: 100 } })]);
    setPerioder(p.data); setAudit(a.data);
    try {
      const s = await api.get('/altinn/status');
      setAltinnStatus(s.data);
    } catch { setAltinnStatus({ konfigurert: false }); }
  };
  useEffect(() => { last(); }, []);

  const lastNedSaft = async () => {
    setBusy(true);
    try {
      const r = await api.get('/saf-t', { params: { fra, til }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SAF-T_${fra}_til_${til}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Eksport feilet.'); }
    setBusy(false);
  };

  const lastNedCsv = async (url, filnavn, params = { fra, til }) => {
    try {
      const r = await api.get(url, { params, responseType: 'blob' });
      const u = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = u; a.download = filnavn; a.click();
      window.URL.revokeObjectURL(u);
    } catch { alert('Eksport feilet.'); }
  };

  const lukkPeriode = async () => {
    if (!nyPeriode.fra_dato || !nyPeriode.til_dato) return alert('Fyll inn datoer.');
    if (!window.confirm(`Lukke perioden ${nyPeriode.fra_dato} – ${nyPeriode.til_dato}? Posteringer kan ikke endres etterpå.`)) return;
    await api.post('/perioder', nyPeriode);
    setNyPeriode({ fra_dato: '', til_dato: '', beskrivelse: '' });
    last();
  };

  const aapnePeriode = async (p) => {
    if (!window.confirm(`Åpne perioden igjen?`)) return;
    await api.delete(`/perioder/${p.id}`);
    last();
  };

  return (
    <>
      <PageHeader title="Compliance" subtitle="SAF-T eksport, periodelås og sporbarhet" />

      {/* SAF-T */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>
          <FileText size={14} /> SAF-T Financial 1.30 (Skatteetaten)
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
          Eksporterer kontoplan, kunder, leverandører, MVA-koder og posteringer i Skatteetatens XML-format.
        </p>
        <div className="flex" style={{ gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div><label>Fra</label><input type="date" value={fra} onChange={(e) => setFra(e.target.value)} /></div>
          <div><label>Til</label><input type="date" value={til} onChange={(e) => setTil(e.target.value)} /></div>
          <button className="btn-primary" onClick={lastNedSaft} disabled={busy}>
            <Download size={14} /> {busy ? 'Genererer…' : 'Last ned SAF-T XML'}
          </button>
        </div>
        <div className="flex" style={{ gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="btn-ghost" onClick={() => lastNedCsv('/rapport/dagbok.csv', `dagbok_${fra}_${til}.csv`)}>
            <Download size={12} /> Dagbok (CSV)
          </button>
          <button className="btn-ghost" onClick={() => lastNedCsv('/rapport/hovedbok.csv', `hovedbok_${fra}_${til}.csv`)}>
            <Download size={12} /> Hovedbok (CSV)
          </button>
          <button className="btn-ghost" onClick={() => lastNedCsv('/rapport/mva-spesifikasjon.csv', `mva_${fra}_${til}.csv`)}>
            <Download size={12} /> MVA-spesifikasjon (CSV)
          </button>
          <button className="btn-ghost" onClick={() => lastNedCsv('/audit.csv', 'audit.csv', {})}>
            <Download size={12} /> Audit-logg (CSV)
          </button>
        </div>
      </Card>

      {/* Periodelås */}
      <Card style={{ marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>
          <Lock size={14} /> Lukkede perioder
        </h3>
        <p className="fm-text-muted" style={{ fontSize: 12, margin: '0 0 12px' }}>
          Lukkede perioder kan ikke endres. Brukes etter mva-rapportering eller årsavslutning.
        </p>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 2fr auto', gap: 8, marginBottom: 12 }}>
          <div><label>Fra</label><input type="date" value={nyPeriode.fra_dato} onChange={(e) => setNyPeriode({ ...nyPeriode, fra_dato: e.target.value })} /></div>
          <div><label>Til</label><input type="date" value={nyPeriode.til_dato} onChange={(e) => setNyPeriode({ ...nyPeriode, til_dato: e.target.value })} /></div>
          <div><label>Beskrivelse</label><input value={nyPeriode.beskrivelse} placeholder="f.eks. MVA 2T 2025" onChange={(e) => setNyPeriode({ ...nyPeriode, beskrivelse: e.target.value })} /></div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="btn-primary" onClick={lukkPeriode}><Plus size={14} /> Lukk periode</button>
          </div>
        </div>
        <table>
          <thead><tr><th>Fra</th><th>Til</th><th>Beskrivelse</th><th>Lukket</th><th></th></tr></thead>
          <tbody>
            {perioder.length === 0 && <tr><td colSpan={5} className="fm-text-muted" style={{ textAlign: 'center', padding: 12 }}>Ingen lukkede perioder.</td></tr>}
            {perioder.map((p) => (
              <tr key={p.id}>
                <td>{dato(p.fra_dato)}</td>
                <td>{dato(p.til_dato)}</td>
                <td>{p.beskrivelse}</td>
                <td className="fm-text-muted">{p.lukket_tid?.slice(0, 16).replace('T', ' ')}</td>
                <td>
                  <button className="btn-ghost" onClick={() => aapnePeriode(p)} style={{ color: 'var(--theme-warning)' }}>
                    <Unlock size={12} /> Åpne
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Audit-logg */}
      <Card style={{ marginBottom: 16, padding: 0 }}>
        <h3 style={{ margin: 0, padding: '10px 14px', fontSize: 14, fontWeight: 600, borderBottom: '1px solid var(--theme-border)' }}>
          Sporingslogg (siste 100)
        </h3>
        <table>
          <thead><tr><th>Tidspunkt</th><th>Bruker</th><th>Handling</th><th>Entitet</th><th>ID</th><th>IP</th></tr></thead>
          <tbody>
            {audit.map((r) => (
              <tr key={r.id}>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{r.tidspunkt?.slice(0, 19).replace('T', ' ')}</td>
                <td>{r.bruker_id || '—'}</td>
                <td><span className={`badge ${r.handling === 'DELETE' ? 'badge-danger' : r.handling === 'CREATE' ? 'badge-success' : r.handling === 'LOCK' ? 'badge-warning' : 'badge-neutral'}`}>{r.handling}</span></td>
                <td>{r.entitet}</td>
                <td>{r.entitet_id}</td>
                <td className="fm-text-muted" style={{ fontSize: 11 }}>{r.ip}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Altinn-status */}
      <Card>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Send size={14} /> Altinn-innsending
          {altinnStatus?.konfigurert
            ? <span className="badge badge-success" style={{ marginLeft: 'auto' }}>
                <CheckCircle2 size={11} /> Konfigurert ({altinnStatus.miljo || 'TT02'})
              </span>
            : <span className="badge badge-warning" style={{ marginLeft: 'auto' }}>
                <AlertTriangle size={11} /> Ikke konfigurert
              </span>}
        </h3>
        {altinnStatus?.konfigurert ? (
          <p className="fm-text-muted" style={{ fontSize: 12, margin: 0 }}>
            Maskinporten OAuth2 er aktiv. MVA-melding og A-melding sendes direkte til Altinn fra
            de respektive fanene. Org.nr: <b>{altinnStatus.org_nr || '—'}</b>.
          </p>
        ) : (
          <div style={{ fontSize: 12 }} className="fm-text-muted">
            <p style={{ margin: '0 0 6px' }}>
              Backend-koden er klar — krever bare at admin setter Maskinporten-secrets:
              <code>ALTINN_MASKINPORTEN_CLIENT_ID</code>, <code>ALTINN_MASKINPORTEN_KEY</code>,
              <code>ALTINN_ORG_NR</code> (+ valgfritt <code>ALTINN_MILJO=tt02</code>/<code>prod</code>).
            </p>
            <p style={{ margin: 0 }}>
              Inntil da: bruk SAF-T-eksporten over og last opp manuelt via altinn.no.
            </p>
          </div>
        )}
      </Card>

      {/* Retention og DPA */}
      <Card style={{ marginTop: 16 }}>
        <h3 style={{ margin: '0 0 8px', fontSize: 14, fontWeight: 600 }}>Retensjons- og personvernpolicy</h3>
        <div className="fm-text-muted" style={{ fontSize: 12, lineHeight: 1.55 }}>
          <p style={{ margin: '0 0 6px' }}>
            <b>Bokføringsloven §13:</b> Regnskapsmateriale og bilag oppbevares i minst <b>5 år</b> etter
            regnskapsårets slutt. Vedlegg lagres permanent på server (UUID-navn) og slettes ikke automatisk
            før retensjonsperioden er utløpt.
          </p>
          <p style={{ margin: '0 0 6px' }}>
            <b>GDPR:</b> Brukere kan eksportere egne data fra Innstillinger → Personvern, og kan
            anonymiseres på forespørsel. Regnskapsdata beholdes pga. bokføringslovens krav, men
            personopplysninger (navn, e-post, passord, 2FA) fjernes ved anonymisering.
          </p>
          <p style={{ margin: '0 0 6px' }}>
            <b>Databehandleravtale (DPA):</b> En standardavtale i tråd med GDPR art. 28 er tilgjengelig
            på forespørsel. Send e-post til support for å få tilsendt mal og signere.
          </p>
          <p style={{ margin: 0 }}>
            <b>Data lagres i:</b> EU (Fly.io ams-region). Backup tas daglig.
            Sletting av en bedriftskonto medfører at alle data slettes etter 30 dager – med unntak
            av regnskapsbilag som beholdes i 5 år iht. bokføringsloven.
          </p>
        </div>
      </Card>
    </>
  );
};

export default Compliance;
